import React, { useEffect, useState } from "react";
import { api } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { Button } from "./ui/button";
import { ChartBar, FilePdf, TrendUp, HandCoins, Warning, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";

const money = (v, cur) => `${cur === "USD" ? "US$" : "RD$"} ${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const waLink = (l) => {
  const digits = String(l.phone || "").replace(/\D/g, "");
  const num = digits.length === 10 ? "1" + digits : digits;
  const msg = `Grupo Estrella Hernández Inversiones - Aviso de cuota.\nEstimado/a ${l.name}, su préstamo ${l.loan_number} tiene un saldo pendiente de ${money(l.remaining, l.currency)} y cuota de ${money(l.monthly_payment, l.currency)}. Le agradecemos ponerse al día. ¡Gracias!`;
  return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
};

export const PortfolioPanel = () => {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = () => {
    setLoading(true);
    setError(false);
    api.get("/admin/portfolio-report").then((r) => setData(r.data)).catch(() => setError(true)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/portfolio-report/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "reporte_cartera.pdf"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error"); }
    finally { setDownloading(false); }
  };

  const currencies = ["DOP", "USD"];

  return (
    <div className="space-y-6" data-testid="portfolio-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-emerald-100 flex items-center justify-center"><ChartBar size={24} weight="duotone" className="text-emerald-800" /></div>
          <div>
            <h2 className="font-display font-bold text-emerald-900 text-xl">{t("portfolio_title")}</h2>
            <p className="text-sm text-slate-500">{t("portfolio_sub")}</p>
          </div>
        </div>
        <Button data-testid="portfolio-pdf-btn" onClick={downloadPdf} disabled={downloading || loading} className="bg-emerald-800 hover:bg-emerald-900 gap-2 h-11"><FilePdf size={18} weight="bold" /> {downloading ? t("generating") : t("download_pdf")}</Button>
      </div>

      {loading ? <p className="text-slate-500">{t("loading")}</p> : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center" data-testid="portfolio-error">
          <p className="text-red-700 font-semibold">{t("portfolio_load_error")}</p>
          <Button data-testid="portfolio-retry" onClick={load} className="mt-3 bg-emerald-800 hover:bg-emerald-900">{t("retry")}</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {currencies.map((c) => {
            const b = (data.by_currency || {})[c] || { lent: 0, collected: 0, pending: 0, loans_count: 0, active_count: 0, paid_off_count: 0, overdue_count: 0, overdue_amount: 0 };
            return (
              <div key={c} className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid={`portfolio-card-${c}`}>
                <h3 className="font-display font-bold text-emerald-900 text-lg mb-4">{c === "USD" ? "US$ (USD)" : "RD$ (DOP)"}</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-emerald-50 p-4">
                    <p className="text-xs uppercase tracking-wider text-emerald-600 flex items-center gap-1.5"><HandCoins size={14} weight="fill" /> {t("total_lent")}</p>
                    <p className="font-mono-num text-xl font-bold text-emerald-900 mt-1" data-testid={`portfolio-lent-${c}`}>{money(b.lent, c)}</p>
                  </div>
                  <div className="rounded-lg bg-teal-50 p-4">
                    <p className="text-xs uppercase tracking-wider text-teal-600 flex items-center gap-1.5"><TrendUp size={14} weight="fill" /> {t("total_collected")}</p>
                    <p className="font-mono-num text-xl font-bold text-teal-800 mt-1">{money(b.collected, c)}</p>
                  </div>
                  <div className="rounded-lg bg-amber-50 p-4">
                    <p className="text-xs uppercase tracking-wider text-amber-600">{t("total_pending")}</p>
                    <p className="font-mono-num text-xl font-bold text-amber-700 mt-1" data-testid={`portfolio-pending-${c}`}>{money(b.pending, c)}</p>
                  </div>
                  <div className="rounded-lg bg-red-50 p-4">
                    <p className="text-xs uppercase tracking-wider text-red-500 flex items-center gap-1.5"><Warning size={14} weight="fill" /> {t("overdue")}</p>
                    <p className="font-mono-num text-xl font-bold text-red-600 mt-1">{money(b.overdue_amount, c)}</p>
                    <p className="text-[11px] text-red-500">{b.overdue_count} {t("loans_lower")}</p>
                  </div>
                </div>
                <div className="flex justify-between text-sm text-slate-500 mt-4 pt-3 border-t border-gray-100">
                  <span>{t("loans_count")}: <b className="text-emerald-900">{b.loans_count}</b></span>
                  <span>{t("active_lower")}: <b className="text-emerald-900">{b.active_count}</b></span>
                  <span>{t("paid_off_lower")}: <b className="text-emerald-900">{b.paid_off_count}</b></span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && !error && (data.overdue_loans || []).length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-x-auto" data-testid="overdue-table">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
            <Warning size={18} weight="fill" className="text-red-500" />
            <h3 className="font-display font-bold text-emerald-900">{t("overdue_loans_title")}</h3>
            <span className="text-xs text-slate-400">({(data.overdue_loans || []).length})</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-gray-100">
                <th className="px-4 py-2.5 font-medium">{t("loan_number")}</th>
                <th className="px-4 py-2.5 font-medium">{t("holder")}</th>
                <th className="px-4 py-2.5 font-medium">{t("phone")}</th>
                <th className="px-4 py-2.5 font-medium text-center">{t("installments_behind")}</th>
                <th className="px-4 py-2.5 font-medium text-right">{t("total_pending")}</th>
                <th className="px-4 py-2.5 font-medium text-right">WhatsApp</th>
              </tr>
            </thead>
            <tbody>
              {data.overdue_loans.map((l, i) => (
                <tr key={l.loan_number || i} className="border-b border-gray-50 last:border-0" data-testid={`overdue-row-${i}`}>
                  <td className="px-4 py-2.5 font-mono-num text-emerald-900">{l.loan_number}</td>
                  <td className="px-4 py-2.5 text-emerald-900 font-semibold">{l.name}</td>
                  <td className="px-4 py-2.5 text-slate-500">{l.phone || "—"}</td>
                  <td className="px-4 py-2.5 text-center"><span className="text-[11px] font-bold px-2 py-0.5 rounded bg-red-100 text-red-600">{l.installments_behind}</span></td>
                  <td className="px-4 py-2.5 text-right font-mono-num font-bold text-amber-700">{money(l.remaining, l.currency)}</td>
                  <td className="px-4 py-2.5 text-right">
                    {l.phone ? (
                      <a data-testid={`overdue-wa-${i}`} href={waLink(l)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-md bg-[#25D366] px-2.5 py-1 text-xs font-semibold text-white hover:bg-[#1eb85a]"><WhatsappLogo size={14} weight="fill" /> {t("send")}</a>
                    ) : <span className="text-slate-300 text-xs">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
